// client/src/coms/e2ee/serverE2eeClient.js
// A server's end-to-end encryption setting as the server reports it (policy: Standard / Encrypted Server, pending
// acknowledgements, history). Whether a channel is actually encrypted on this device is decided separately, from the
// device's keys (useChannelE2ee.js); this module never produces a lock by itself.
import { useCallback, useEffect, useState } from 'react';
import { comsApiGet, comsApiPost } from '../comsApi';

const EVENT = 'vox-server-e2ee-changed';
const cache = new Map(); // serverKey -> { at, data }
const MAX_AGE_MS = 15000;

export function serverE2eeChanged(serverKey) {
  cache.delete(String(serverKey || ''));
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { serverKey: String(serverKey || '') } }));
  } catch {}
}

export async function fetchServerE2ee(serverKey, { fresh = false } = {}) {
  const key = String(serverKey || '');
  if (!key) return null;
  const hit = cache.get(key);
  if (!fresh && hit && Date.now() - hit.at < MAX_AGE_MS) return hit.data;
  const data = await comsApiGet(`/servers/${encodeURIComponent(key)}/e2ee`);
  cache.set(key, { at: Date.now(), data });
  return data;
}

export async function setServerE2ee(serverKey, enabled) {
  const data = await comsApiPost(`/servers/${encodeURIComponent(serverKey)}/e2ee`, { enabled: !!enabled, confirm: true });
  serverE2eeChanged(serverKey);
  return data;
}

export async function ackServerE2ee(serverKey, version) {
  const data = await comsApiPost(`/servers/${encodeURIComponent(serverKey)}/e2ee/ack`, { version });
  serverE2eeChanged(serverKey);
  return data;
}

export async function authoriseE2eeAdmin(serverKey, userId, authorised) {
  const data = await comsApiPost(`/servers/${encodeURIComponent(serverKey)}/e2ee/admins`, { user_id: userId, authorised: !!authorised });
  serverE2eeChanged(serverKey);
  return data;
}

export function useServerE2ee(serverKey) {
  const key = String(serverKey || '');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const refresh = useCallback(
    (fresh = true) =>
      key
        ? fetchServerE2ee(key, { fresh })
            .then((d) => {
              setData(d);
              setError('');
              return d;
            })
            .catch((e) => setError(String((e && e.message) || e)))
        : Promise.resolve(null),
    [key]
  );
  useEffect(() => {
    setData(null);
    refresh(false);
    const onChange = (e) => {
      if (!e || !e.detail || !e.detail.serverKey || e.detail.serverKey === key) refresh(true);
    };
    window.addEventListener(EVENT, onChange);
    const t = setInterval(() => refresh(true), 30000);
    return () => {
      window.removeEventListener(EVENT, onChange);
      clearInterval(t);
    };
  }, [key, refresh]);
  return { data, error, refresh };
}
